/**
 * SocialPostCard Component
 *
 * Card display for social post in list views.
 * Shows thumbnail, title, platforms, status, and approval progress.
 */
import { useNavigate } from 'react-router-dom';
import {
  Facebook,
  Instagram,
  Music2,
  Clock,
  CheckCircle2,
  XCircle,
  Send,
  Edit3,
  MoreHorizontal,
  Copy,
  Trash2,
  Eye,
} from 'lucide-react';

import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

const PLATFORM_ICONS = {
  facebook: Facebook,
  instagram: Instagram,
  tiktok: Music2,
};

const PLATFORM_COLORS = {
  facebook: 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30',
  instagram: 'text-pink-600 dark:text-pink-400 bg-pink-50 dark:bg-pink-900/30',
  tiktok: 'text-gray-900 dark:text-white bg-gray-100 dark:bg-gray-800',
};

const STATUS_CONFIG = {
  draft: {
    label: 'Draft',
    icon: Edit3,
    variant: 'secondary',
    className: 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300',
  },
  pending_review: {
    label: 'Pending Review',
    icon: Clock,
    variant: 'secondary',
    className: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300',
  },
  approved: {
    label: 'Approved',
    icon: CheckCircle2,
    variant: 'secondary',
    className: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300',
  },
  scheduled: {
    label: 'Scheduled',
    icon: Clock,
    variant: 'secondary',
    className: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
  },
  publishing: {
    label: 'Publishing...',
    icon: Send,
    variant: 'secondary',
    className: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 animate-pulse',
  },
  published: {
    label: 'Published',
    icon: CheckCircle2,
    variant: 'secondary',
    className: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300',
  },
  failed: {
    label: 'Failed',
    icon: XCircle,
    variant: 'destructive',
    className: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300',
  },
};

const SocialPostCard = ({
  post,
  onDuplicate,
  onDelete,
  onClick,
}) => {
  const navigate = useNavigate();
  const statusConfig = STATUS_CONFIG[post.status] || STATUS_CONFIG.draft;
  const StatusIcon = statusConfig.icon;

  const handleClick = () => {
    if (onClick) {
      onClick(post);
    } else {
      navigate(`/admin/outreach/social/${post.id}`);
    }
  };

  const handleDuplicate = (e) => {
    e.stopPropagation();
    onDuplicate?.(post);
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    onDelete?.(post);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  return (
    <Card
      data-testid="social-post-card"
      className="cursor-pointer hover:shadow-md transition-shadow"
      onClick={handleClick}
    >
      <CardContent className="p-4">
        <div className="flex gap-4">
          {/* Thumbnail */}
          <div className="flex-shrink-0 w-24 h-24 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800">
            {post.thumbnail_url ? (
              <img
                src={post.thumbnail_url}
                alt=""
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-400 dark:text-gray-500 dark:text-gray-400">
                <Eye className="h-8 w-8" />
              </div>
            )}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            {/* Title and Status */}
            <div className="flex items-start justify-between gap-2">
              <h3 className="font-semibold text-gray-900 dark:text-white truncate">
                {post.title}
              </h3>
              <Badge className={cn('flex-shrink-0', statusConfig.className)}>
                <StatusIcon className="h-3 w-3 mr-1" />
                {statusConfig.label}
              </Badge>
            </div>

            {/* Caption preview */}
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 line-clamp-2">
              {post.caption || 'No caption'}
            </p>

            {/* Meta row */}
            <div className="mt-3 flex items-center justify-between">
              {/* Platforms */}
              <div className="flex items-center gap-1">
                {post.target_platforms?.map((platform) => {
                  const Icon = PLATFORM_ICONS[platform];
                  const colors = PLATFORM_COLORS[platform];
                  return Icon ? (
                    <div
                      key={platform}
                      className={cn('p-1.5 rounded-md', colors)}
                    >
                      <Icon className="h-4 w-4" />
                    </div>
                  ) : null;
                })}
              </div>

              {/* Approval progress */}
              {post.status === 'pending_review' && (
                <div className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400">
                  <CheckCircle2 className="h-4 w-4" />
                  <span>
                    {post.approval_count}/{post.required_approvals} approvals
                  </span>
                </div>
              )}

              {/* Scheduled time */}
              {post.status === 'scheduled' && post.scheduled_for && (
                <div className="flex items-center gap-1 text-sm text-blue-600 dark:text-blue-400">
                  <Clock className="h-4 w-4" />
                  <span>{formatDate(post.scheduled_for)}</span>
                </div>
              )}

              {/* Actions */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                  <Button variant="ghost" size="icon" className="h-8 w-8">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={handleDuplicate}>
                    <Copy className="h-4 w-4 mr-2" />
                    Duplicate
                  </DropdownMenuItem>
                  {post.status === 'draft' && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={handleDelete}
                        className="text-red-600"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default SocialPostCard;
